import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Typography, Link as MuiLink } from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import { ReactNode } from 'react';
import { brand } from '../theme';

export const SIDEBAR_WIDTH = 258;

interface NavDef {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
}

const NAV: NavDef[] = [
  { to: '/', label: 'Tổng quan', icon: <HomeRoundedIcon />, end: true },
  { to: '/debtSlips', label: 'Đơn nợ', icon: <ReceiptLongRoundedIcon /> },
  { to: '/vatInvoices', label: 'Hóa đơn VAT', icon: <DescriptionRoundedIcon /> },
  { to: '/paymentOrders', label: 'Lệnh chi', icon: <PaymentsRoundedIcon /> },
  { to: '/suppliers', label: 'Nhà cung cấp', icon: <StoreRoundedIcon /> },
  { to: '/settings', label: 'Cài đặt', icon: <SettingsRoundedIcon /> },
];

const NavItem = ({ item, onNavigate }: { item: NavDef; onNavigate?: () => void }) => {
  const { pathname } = useLocation();
  const active = item.end ? pathname === '/' : pathname.startsWith(item.to);
  return (
    <MuiLink
      component={RouterLink}
      to={item.to}
      onClick={onNavigate}
      underline="none"
      aria-current={active ? 'page' : undefined}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 1.4,
        minHeight: 48,
        px: 1.7,
        py: 1,
        borderRadius: '12px',
        color: active ? brand.sidebarTextActive : brand.sidebarText,
        background: active ? brand.activePill : 'transparent',
        boxShadow: active ? '0 12px 26px -18px rgba(14,116,144,.85)' : 'none',
        fontWeight: 700,
        transition: 'background .2s ease, color .2s ease, transform .2s ease',
        '&:hover': {
          color: '#fff',
          background: active ? brand.activePill : 'rgba(255,255,255,.065)',
          transform: 'translateX(2px)',
        },
        '& svg': { fontSize: 21 },
      }}
    >
      {item.icon}
      <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.label}</Typography>
    </MuiLink>
  );
};

const Logo = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 0.5, py: 0.5 }}>
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: '12px',
        display: 'grid',
        placeItems: 'center',
        color: '#fff',
        background: brand.avatarGradient,
        border: '1px solid rgba(255,255,255,.20)',
        boxShadow: '0 14px 24px -16px rgba(22,135,155,.85)',
      }}
    >
      <AccountBalanceWalletRoundedIcon sx={{ fontSize: 23 }} />
    </Box>
    <Box>
      <Typography sx={{ color: '#fff', fontWeight: 850, fontSize: 20, letterSpacing: '-0.025em', lineHeight: 1.05 }}>
        AI Công Nợ
      </Typography>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.48)', fontWeight: 600 }}>
        Trí tuệ tài chính
      </Typography>
    </Box>
  </Box>
);

export const AppSidebar = ({ onNavigate }: { onNavigate?: () => void }) => (
  <Box
    sx={{
      width: SIDEBAR_WIDTH,
      minWidth: SIDEBAR_WIDTH,
      height: '100dvh',
      minHeight: 0,
      background: brand.sidebarGradient,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      p: 1.8,
      position: 'relative',
      overflowX: 'hidden',
      overflowY: 'auto',
      '&::after': {
        content: '""',
        position: 'absolute',
        width: 190,
        height: 190,
        borderRadius: '50%',
        right: -110,
        bottom: 80,
        background: 'rgba(22,135,155,.10)',
        filter: 'blur(8px)',
        pointerEvents: 'none',
      },
    }}
  >
    <Box sx={{ mb: 1.8, pb: 1.8, borderBottom: '1px solid rgba(255,255,255,.075)', position: 'relative', zIndex: 1 }}>
      <Logo />
    </Box>
    <Box component="nav" aria-label="Điều hướng chính" sx={{ display: 'flex', flexDirection: 'column', gap: 0.45, position: 'relative', zIndex: 1 }}>
      {NAV.map((item) => <NavItem key={item.to} item={item} onNavigate={onNavigate} />)}
    </Box>
  </Box>
);
